import {
  ActionList,
  ActionListItem,
  ActionListItemBadge,
  Button,
  Divider,
  Dropdown,
  DropdownOverlay,
  Link,
  SelectInput,
  TextArea,
  TextInput,
} from '@razorpay/blade/components';
import { routePaths } from 'components/Routes/data';
import { salaryComponentParams } from 'components/SalaryComponents/constants';
import { useComponentParams, useDetailsView } from 'components/SalaryComponents/hooks';
import { EarningPayType } from 'components/SalaryComponents/types';
import {
  FieldGroup,
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
  StepHeader,
  SwitchDescription,
  SwitchField,
} from 'components/WizardViews/components';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import {
  earningsGeneralDetailsStepValidationSchema,
  earningSteps,
  getEarningPayTypes,
  knowMoreItems,
} from 'components/WizardViews/SalaryComponents/constants';
import { EarningsFormData } from 'components/WizardViews/SalaryComponents/types';
import { checkIsPayFrequencyAdhoc } from 'components/WizardViews/SalaryComponents/utils';
import { useFormWithProvider } from 'forms';
import useFeatures from 'hooks/useFeatures';
import React, { FormEvent } from 'react';
import { useHistory } from 'react-router-dom';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';

const GeneralDetailsStep: React.FC = () => {
  const { validate, formData, setFieldValue, register } = useFormWithProvider<EarningsFormData>();
  const { features } = useFeatures();
  const { updateStepStatus } = useWizardInfo();
  const history = useHistory();
  const { searchString } = useComponentParams();
  const { openDetailsView } = useDetailsView(salaryComponentParams.knowMore);
  const { isM3S1Disabled } = useIsFeatureDisabledForRearch();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validate(earningsGeneralDetailsStepValidationSchema)) {
      updateStepStatus(earningSteps.step1, 'success');
      history.push({
        pathname: routePaths.salaryComponents.create.earnings.taxationDetails,
        search: searchString,
      });
    }
  };

  const isPayFrequencyAdhoc = checkIsPayFrequencyAdhoc(formData.payType);

  const handlePayFrequencyChange = (value: EarningPayType) => {
    setFieldValue('payType', value);
    if (value === 'adhoc') {
      setFieldValue('supportProration', false);
      setFieldValue('considerInArrear', false);
    }
  };

  const earningPayTypes = getEarningPayTypes(features);

  //   TODO add validation for component name from server

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <StepContentWrapper>
        <StepHeader title="General & pay" subtitle="Configuration" marginBottom={'spacing.9'} />

        <FieldGroup title="Basic Details">
          <TextInput
            label="Component name"
            placeholder="Component name"
            {...register('name')}
            maxCharacters={50}
            necessityIndicator={'required'}
            isRequired
          />
          <TextInput
            label="Display name"
            placeholder="Display name"
            helpText="Customise how you'd like the components' names to appear on your employees' payslips and in the salary register."
            {...register('displayName')}
            maxCharacters={50}
            necessityIndicator={'required'}
          />
          <TextArea
            label="Description"
            placeholder="Description"
            {...register('description')}
            maxCharacters={255}
          />
        </FieldGroup>

        <Divider marginY="spacing.8" />

        <FieldGroup
          title="Pay Frequency"
          subtitle="Choose how often employees receive this component as their pay.">
          <Dropdown>
            <SelectInput
              label="Pay frequency"
              placeholder="Pay frequency"
              {...register('payType', 'select')}
              onChange={({ values }) => handlePayFrequencyChange(values[0] as EarningPayType)}
            />
            <DropdownOverlay>
              <ActionList>
                {Object.entries(earningPayTypes).map(([key, item]) => (
                  <ActionListItem
                    key={key}
                    title={item.label}
                    value={key}
                    description={item.description}
                    isDisabled={item.isComingSoon}
                    titleSuffix={
                      item.isComingSoon ? (
                        <ActionListItemBadge color="notice">Coming soon</ActionListItemBadge>
                      ) : undefined
                    }
                  />
                ))}
              </ActionList>
            </DropdownOverlay>
          </Dropdown>
        </FieldGroup>

        <Divider marginY="spacing.8" />

        <FieldGroup title="Proration & Arrears">
          <SwitchField
            label="Prorate the component"
            accessibilityLabel="Proration"
            size="small"
            {...register('supportProration', 'switch')}
            isDisabled={isPayFrequencyAdhoc}
            tooltip={isPayFrequencyAdhoc ? 'Ad hoc components cannot be prorated' : ''}>
            <SwitchDescription content="The pay will be adjusted based on the number of working days, such as for loss of pay, joining date." />
            <Link
              variant={'button'}
              marginLeft="spacing.2"
              size="small"
              onClick={() => openDetailsView(knowMoreItems.proration)}
              accessibilityLabel={'know more - proration'}>
              Know more✨
            </Link>
          </SwitchField>
          <SwitchField
            label="Calculate arrears"
            accessibilityLabel="Calculate Arrears"
            size="small"
            {...register('considerInArrear', 'switch')}
            isDisabled={isM3S1Disabled}>
            <SwitchDescription content="Choose whether to calculate arrears for this component in case of missed payments or salary revisions." />
            <Link
              variant={'button'}
              marginLeft="spacing.2"
              size="small"
              onClick={() => openDetailsView(knowMoreItems.arrearCalculation)}
              accessibilityLabel={'know more - arrears'}>
              Know more✨
            </Link>
          </SwitchField>
        </FieldGroup>
      </StepContentWrapper>
      <StepActionsWrapper>
        <span />
        <Button type="submit">Next: Taxation</Button>
      </StepActionsWrapper>
    </FormWrapper>
  );
};

export default GeneralDetailsStep;
